/**
 * F-per-company — Gestão de usuários.
 *
 * F-admin-usuarios (CEO 2026-11-02): o Administrador de empresa também entra
 * aqui (rota requireAdmin). Vê só as contas das empresas que administra, cria
 * conta com link de senha por e-mail (sem digitar senha), nunca dá o papel de
 * Administrador e não mexe em conta de Administrador / Super Admin. Super
 * admin, personificar, senha digitada e excluir continuam só do Super Admin.
 *
 * Modelo POR EMPRESA:
 *   - Super admin (flag GLOBAL): acesso irrestrito a tudo, todas as empresas
 *     (inclui o módulo Financeiro/StormFin). Ignora roles por empresa.
 *   - Por empresa, cada usuário tem (ou não) uma role:
 *       Administrador (gestão) · Usuário (operacional) · Visualizador (leitura)
 *       ou "Sem acesso".
 *
 * Mutations: users.setSuperAdmin (flag global), users.addMembership /
 * users.removeMembership (role por empresa).
 */

import { useState } from "react";
import { Link } from "wouter";
import { useModals } from "../components/common/ModalsProvider.js";
import { SenhaLinkModal, type SenhaLinkResultado } from "../components/domain/SenhaLinkModal.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";
import { ERP_MENUS, type ErpMenu, type MenuGrantLevel } from "@eurostorm/shared";

type Role = "technician" | "manager" | "client" | "viewer" | "user" | "admin";
/** F-montador-role — valor do dropdown: role, ou "technician:montage". */
type SelectValue = Role | "technician:montage";

const inputCls =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none";

export default function Users() {
  const { user } = useAuth();
  const modals = useModals();
  const utils = trpc.useUtils();

  const isSuper = !!user?.isSuperAdmin;
  const empresasAdmin = new Set(
    (user?.memberships ?? []).filter((m) => m.active && m.role === "admin").map((m) => m.companyId),
  );
  const podeVer = isSuper || empresasAdmin.size > 0;
  const usersQuery = trpc.users.listAll.useQuery(undefined, { enabled: podeVer });
  const companiesQuery = trpc.companies.list.useQuery(undefined, {
    enabled: podeVer,
  });

  const onErr = (e: { message: string }) =>
    void modals.alert({ title: t("users.errorTitle"), message: e.message, tone: "danger" });
  const invalidate = () => utils.users.listAll.invalidate();

  const setSuperMut = trpc.users.setSuperAdmin.useMutation({
    onSuccess: invalidate,
    onError: onErr,
  });
  const addMembershipMut = trpc.users.addMembership.useMutation({
    onSuccess: async (data) => {
      await invalidate();
      // F-montador-role — sem cadastro de técnico na empresa não há onde
      // gravar a função; avisa em vez de falhar em silêncio.
      if (data.technicianMissing) {
        void modals.alert({
          title: t("users.technicianMissingTitle"),
          message: t("users.technicianMissingMsg"),
          tone: "danger",
        });
      } else if (data.technicianCreated) {
        // F-tech-autocreate — a fiche nasceu junto com o acesso; só faltam os pares.
        void modals.alert({
          title: t("users.technicianCreatedTitle"),
          message: `${data.technicianCreated.name} — ${t("users.technicianCreatedMsg")}`,
          tone: "default",
        });
      }
    },
    onError: onErr,
  });
  const removeMembershipMut = trpc.users.removeMembership.useMutation({
    onSuccess: invalidate,
    onError: onErr,
  });
  // F-acesso-por-menu (CEO 2026-10-12) — menus do Gestion para papéis de portal.
  const [grantsFor, setGrantsFor] = useState<{ userId: number; companyId: number } | null>(null);
  const setMenuGrantsMut = trpc.users.setMenuGrants.useMutation({
    onSuccess: async () => {
      await invalidate();
      setGrantsFor(null);
      void modals.alert({ title: t("users.menusTitle"), message: t("users.menusSaved") });
    },
    onError: onErr,
  });
  const setActiveMut = trpc.users.setActive.useMutation({ onSuccess: invalidate, onError: onErr });
  // F-user-delete — excluir libera o nome/e-mail para uma conta nova.
  // F-admin-reset-password — super admin define senha nova (não há "ver senha":
  // hashes são irreversíveis; o poder do admin é redefinir).
  const resetPasswordMut = trpc.users.resetPassword.useMutation({
    onSuccess: (r) => {
      void modals.alert({
        title: t("users.resetPwDoneTitle"),
        message: `${t("users.resetPwDonePre")} ${r.email}. ${t("users.resetPwDoneSuf")}`,
      });
    },
    onError: onErr,
  });
  async function handleResetPassword(u: { id: number; name: string; email: string }) {
    // F-users-pw-retry (CEO 2026-09-20) — senha curta devolvia para a LISTA e
    // perdia o trabalho; agora o aviso volta para o mesmo formulário até a
    // senha servir ou o operador cancelar.
    for (;;) {
      const pw = await modals.promptReason({
        title: `${t("users.resetPwTitle")} — ${u.name}`,
        placeholder: t("users.resetPwPlaceholder"),
        required: true,
      });
      if (!pw) return;
      if (pw.trim().length >= 8) {
        resetPasswordMut.mutate({ userId: u.id, newPassword: pw.trim() });
        return;
      }
      await modals.alert({
        title: t("users.resetPwTitle"),
        message: t("users.resetPwTooShort"),
        tone: "danger",
      });
    }
  }
  // F-impersonate-tab — abre o app como o usuário-alvo em NOVA ABA (token
  // por aba via sessionStorage; a sessão de admin desta aba fica intacta).
  const impersonateMut = trpc.users.impersonate.useMutation({ onError: onErr });
  function handleImpersonate(u: { id: number; name: string }) {
    // A aba precisa abrir SINCRONAMENTE no clique (política de pop-ups);
    // navegamos nela quando o token chegar.
    const tab = window.open("about:blank", "_blank");
    impersonateMut.mutate(
      { userId: u.id },
      {
        onSuccess: (r) => {
          const url = `${r.landing}#imp=${encodeURIComponent(r.token)}`;
          if (tab) tab.location.replace(url);
          else window.open(url, "_blank");
        },
        onError: (e) => {
          tab?.close();
          onErr(e);
        },
      },
    );
  }
  const softDeleteMut = trpc.users.softDelete.useMutation({
    onSuccess: (res) => {
      invalidate();
      void modals.alert({
        title: t("users.deleteDoneTitle"),
        message: `${t("users.deleteDonePre")} ${res.freedEmail} ${t("users.deleteDoneSuf")}`,
      });
    },
    onError: onErr,
  });
  const createMut = trpc.users.createDirect.useMutation();
  // F-admin-usuarios — conta nova sem senha digitada + reenvio do link.
  const createLinkMut = trpc.users.createWithLink.useMutation();
  const sendLinkMut = trpc.users.sendPasswordLink.useMutation({ onError: onErr });
  const [linkResultado, setLinkResultado] = useState<SenhaLinkResultado | null>(null);
  const [locale, setLocale] = useState<"fr" | "pt" | "en" | "es" | "uk">("fr");
  const [createRolesLink, setCreateRolesLink] = useState<Record<number, SelectValue | "none">>({});
  // F-user-edit (CEO 2026-09-19) — editar nome/e-mail pelo modal.
  const updateProfileMut = trpc.users.updateProfile.useMutation({
    onSuccess: () => {
      invalidate();
      setEditing(null);
      void modals.alert({
        title: t("users.editSavedTitle"),
        message: t("users.editSavedMsg"),
      });
    },
    onError: onErr,
  });
  const [editing, setEditing] = useState<{
    id: number;
    name: string;
    email: string;
    /** F-admin-usuarios — conta já usada: e-mail só o Super Admin troca. */
    emailLocked?: boolean;
  } | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [createSuper, setCreateSuper] = useState(false);
  const [createRoles, setCreateRoles] = useState<Record<number, Role | "none">>({});
  const [creating, setCreating] = useState(false);

  const t = useT();
  // F-montador-role — no portal existem DUAS funções de técnico: o PDR
  // (débosselage) e o montador (desmontagem/montagem). A membership é a mesma
  // ("technician"); a função vive no registro de técnico, então o dropdown usa
  // o valor composto "technician:montage".
  const roleOpts: ReadonlyArray<{ value: SelectValue; label: string }> = [
    // F-tech-portal — papel do portal do técnico (nenhum acesso ao ERP).
    { value: "technician", label: t("users.roleTechnicianPdr") },
    { value: "technician:montage", label: t("users.roleTechnicianMontador") },
    { value: "manager", label: t("users.roleManager") },
    { value: "client", label: t("users.roleClient") },
    { value: "viewer", label: t("users.roleViewer") },
    { value: "user", label: t("users.roleUser") },
    { value: "admin", label: t("users.roleAdmin") },
  ];

  // F-users-compact — sigla curta da empresa (ES/BS) para a linha caber.
  const shortCo = (n: string) =>
    /euro/i.test(n)
      ? "ES"
      : /brazilian/i.test(n)
        ? "BS"
        : n
            .split(/\s+/)
            .map((w) => w[0] ?? "")
            .join("")
            .slice(0, 3)
            .toUpperCase();

  if (!user) return null;
  if (!podeVer) {
    return (
      <Shell title={t("users.pageTitle")}>
        <p className="text-sm text-neutral-400">{t("users.accessRestricted")}</p>
      </Shell>
    );
  }

  const companies = (companiesQuery.data ?? []).filter((c) => isSuper || empresasAdmin.has(c.id));
  const users = usersQuery.data ?? [];

  /** F-admin-usuarios — cria a conta e manda o link "defina sua senha". */
  async function createUserWithLink() {
    const memberships = Object.entries(createRolesLink)
      .filter(([, r]) => r !== "none")
      .map(([cid, r]) => {
        const montador = r === "technician:montage";
        const tecnico = r === "technician" || montador;
        return {
          companyId: Number(cid),
          role: (tecnico ? "technician" : r) as Role,
          ...(tecnico
            ? { parcServiceRole: montador ? ("montage" as const) : ("debosselage" as const) }
            : {}),
        };
      });
    if (!name.trim() || !email.trim()) {
      void modals.alert({
        title: t("users.missingDataTitle"),
        message: t("users.linkMissingData"),
        tone: "danger",
      });
      return;
    }
    if (memberships.length === 0) {
      void modals.alert({
        title: t("users.missingDataTitle"),
        message: t("users.needOneAccess"),
        tone: "danger",
      });
      return;
    }
    try {
      const r = await createLinkMut.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        locale,
        memberships,
      });
      await invalidate();
      setShowCreate(false);
      setName("");
      setEmail("");
      setCreateRolesLink({});
      setLinkResultado({
        titulo: t("users.linkSentTitle"),
        nome: r.name,
        email: r.email,
        emailEnviado: r.emailEnviado,
        link: r.link,
      });
    } catch (e) {
      void modals.alert({
        title: t("users.createErrorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    }
  }

  function handleSendLink(u: { id: number; name: string; email: string }) {
    void modals
      .confirm({
        title: t("users.sendLinkTitle"),
        message: `${t("users.sendLinkConfirmPre")} ${u.name} (${u.email}) ${t("users.sendLinkConfirmSuf")}`,
        confirmLabel: t("users.sendLinkBtn"),
      })
      .then(async (ok) => {
        if (!ok) return;
        const r = await sendLinkMut.mutateAsync({ userId: u.id }).catch(() => null);
        if (!r) return;
        setLinkResultado({
          titulo: t("users.linkResentTitle"),
          nome: u.name,
          email: r.email,
          emailEnviado: r.emailEnviado,
          link: r.link,
        });
      });
  }

  async function createUser() {
    if (!name.trim() || !email.trim() || password.length < 8) {
      void modals.alert({
        title: t("users.missingDataTitle"),
        message: t("users.missingDataMessage"),
        tone: "danger",
      });
      return;
    }
    const memberships = createSuper
      ? []
      : Object.entries(createRoles)
          .filter(([, r]) => r !== "none")
          .map(([cid, r]) => ({ companyId: Number(cid), role: r as Role }));
    setCreating(true);
    try {
      const created = await createMut.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        password,
        isSuperAdmin: createSuper,
        memberships,
      });
      await invalidate();
      setShowCreate(false);
      setName("");
      setEmail("");
      setPassword("");
      setCreateSuper(false);
      setCreateRoles({});
      void modals.alert({
        title: t("users.userCreatedTitle"),
        message: `${created.name} ${t("users.userCreatedSuffix")}`,
      });
    } catch (e) {
      void modals.alert({
        title: t("users.createErrorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    } finally {
      setCreating(false);
    }
  }

  function changeCompanyRole(userId: number, companyId: number, value: SelectValue | "none") {
    if (value === "none") {
      removeMembershipMut.mutate({ companyId, userId });
      return;
    }
    // F-montador-role — "technician:montage" é membership de técnico + função
    // de montador no cadastro do técnico.
    const isMontador = value === "technician:montage";
    addMembershipMut.mutate({
      companyId,
      userId,
      role: isMontador ? "technician" : (value as Role),
      ...(value === "technician" || isMontador
        ? { parcServiceRole: isMontador ? ("montage" as const) : ("debosselage" as const) }
        : {}),
    });
  }

  const busy = addMembershipMut.isPending || removeMembershipMut.isPending || setSuperMut.isPending;

  return (
    <Shell title={t("users.pageTitle")}>
      <div className="flex items-center gap-3">
        <p className="text-sm text-neutral-400">
          {t("users.introPre")}
          <strong>{t("users.introStrong")}</strong>
          {t("users.introSuf")}
        </p>
        <div className="flex-1" />
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-sm"
        >
          {showCreate ? t("users.cancelBtn") : t("users.newUserBtn")}
        </button>
        {isSuper && (
          <Link
            href="/convites"
            className="rounded-md border border-neutral-800 hover:border-lime hover:text-lime px-3 py-1.5 text-sm transition-colors"
          >
            {t("users.inviteByEmail")}
          </Link>
        )}
      </div>
      {!isSuper && (
        <p className="text-xs text-neutral-500" data-testid="users-admin-scope">
          {t("users.adminScopeNote")}
        </p>
      )}

      {showCreate && !isSuper && (
        <div
          className="rounded-lg border border-lime/20 bg-lime/[0.03] p-4 space-y-3"
          data-testid="user-create-link-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={t("users.nameLabel")}>
              <input
                name="new-user-name"
                autoComplete="off"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls + " w-full"}
                data-testid="user-create-name"
              />
            </Field>
            <Field label={t("users.emailLabel")}>
              <input
                type="email"
                name="new-user-email"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls + " w-full"}
                data-testid="user-create-email"
              />
            </Field>
            <Field label={t("users.localeLabel")}>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value as typeof locale)}
                className={inputCls + " w-full"}
                data-testid="user-create-locale"
              >
                <option value="fr">Français</option>
                <option value="pt">Português</option>
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="uk">Українська</option>
              </select>
            </Field>
          </div>
          <div>
            <p className="text-[11px] text-neutral-400 mb-1">{t("users.accessPerCompany")}</p>
            <div className="flex flex-wrap gap-3">
              {companies.map((c) => (
                <div key={c.id} className="flex items-center gap-2">
                  <span className="text-xs text-neutral-300">{c.name}</span>
                  <select
                    value={createRolesLink[c.id] ?? "none"}
                    onChange={(e) =>
                      setCreateRolesLink((prev) => ({
                        ...prev,
                        [c.id]: e.target.value as SelectValue | "none",
                      }))
                    }
                    className={inputCls + " text-xs py-1"}
                    data-testid={`user-create-role-${c.id}`}
                  >
                    <option value="none">{t("users.noAccess")}</option>
                    {roleOpts
                      .filter((r) => r.value !== "admin")
                      .map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-neutral-500">{t("users.createLinkHint")}</p>
          <button
            onClick={() => void createUserWithLink()}
            disabled={createLinkMut.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
            data-testid="user-create-link"
          >
            {createLinkMut.isPending ? t("users.creatingBtn") : t("users.createLinkBtn")}
          </button>
        </div>
      )}

      {showCreate && isSuper && (
        <div className="rounded-lg border border-lime/20 bg-lime/[0.03] p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* CEO 2026-10-06: o navegador tratava e-mail + senha como tela de
                LOGIN e preenchia com a conta salva de quem está criando (o
                e-mail da Euro Storm). "new-password" + nomes próprios dizem que
                é o cadastro de OUTRA pessoa — o gerenciador de senhas não
                preenche. */}
            <Field label={t("users.nameLabel")}>
              <input
                name="new-user-name"
                autoComplete="off"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls + " w-full"}
              />
            </Field>
            <Field label={t("users.emailLabel")}>
              <input
                type="email"
                name="new-user-email"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls + " w-full"}
              />
            </Field>
            <Field label={t("users.passwordLabel")}>
              <input
                type="password"
                name="new-user-password"
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls + " w-full"}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-300">
            <input
              type="checkbox"
              checked={createSuper}
              onChange={(e) => setCreateSuper(e.target.checked)}
              className="accent-lime"
            />
            {t("users.superAdminCheckbox")}
          </label>
          {!createSuper && (
            <div>
              <p className="text-[11px] text-neutral-400 mb-1">{t("users.accessPerCompany")}</p>
              <div className="flex flex-wrap gap-3">
                {companies.map((c) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <span className="text-xs text-neutral-300">{c.name}</span>
                    <select
                      value={createRoles[c.id] ?? "none"}
                      onChange={(e) =>
                        setCreateRoles((prev) => ({
                          ...prev,
                          [c.id]: e.target.value as Role | "none",
                        }))
                      }
                      className={inputCls + " text-xs py-1"}
                    >
                      <option value="none">{t("users.noAccess")}</option>
                      {/* F-montador-role — montador só depois que existir o
                          cadastro de técnico; aqui o usuário ainda nem existe. */}
                      {roleOpts
                        .filter((r) => r.value !== "technician:montage")
                        .map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={createUser}
            disabled={creating}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {creating ? t("users.creatingBtn") : t("users.createBtn")}
          </button>
        </div>
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {usersQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("users.loading")}</p>
        )}
        {usersQuery.error && <p className="p-4 text-sm text-red-400">{usersQuery.error.message}</p>}
        {users.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2">{t("users.colName")}</th>
                {isSuper && (
                  <th className="text-center px-3 py-2 w-16">{t("users.colSuperAdmin")}</th>
                )}
                <th className="text-left px-3 py-2">{t("users.colAccessPerCompany")}</th>
                <th className="text-left px-3 py-2 w-20">{t("users.colStatus")}</th>
                <th className="px-3 py-2 text-right">{t("users.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {users.map((u) => {
                const isSelf = u.id === user.id;
                // F-admin-usuarios — o Administrador só mexe em conta que pode gerir.
                const travado = isSelf || !u.canManage;
                // F-montador-role — o select mostra a função real: técnico com
                // parc_service_role "montage" aparece como Montador.
                const roleByCompany = new Map<number, SelectValue>(
                  u.memberships.map((m) => [
                    m.companyId,
                    m.role === "technician" && m.parcServiceRole === "montage"
                      ? ("technician:montage" as const)
                      : (m.role as Role),
                  ]),
                );
                return (
                  <tr
                    key={u.id}
                    className={u.active ? "" : "opacity-60"}
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className="px-3 py-1.5">
                      <span className="font-medium">{u.name}</span>{" "}
                      {isSelf && <span className="text-xs text-lime">{t("users.youTag")}</span>}
                      <span className="block text-xs text-neutral-500">{u.email}</span>
                    </td>
                    {isSuper && (
                      <td className="px-3 py-1.5 text-center">
                        <input
                          type="checkbox"
                          checked={u.isSuperAdmin}
                          disabled={isSelf || busy}
                          onChange={(e) =>
                            setSuperMut.mutate({ userId: u.id, value: e.target.checked })
                          }
                          title={isSelf ? t("users.cannotChangeOwnSuperAdmin") : undefined}
                          className="accent-lime disabled:opacity-40"
                        />
                      </td>
                    )}
                    <td className="px-3 py-1.5">
                      {u.isSuperAdmin ? (
                        <span className="text-xs text-lime">
                          {t("users.fullAccessAllCompanies")}
                        </span>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          {companies.map((c) => (
                            <div key={c.id} className="flex items-center gap-1" title={c.name}>
                              <span className="text-[10px] font-semibold text-neutral-400">
                                {shortCo(c.name)}
                              </span>
                              <select
                                value={roleByCompany.get(c.id) ?? "none"}
                                disabled={travado || busy}
                                data-testid={`user-role-${u.id}-${c.id}`}
                                onChange={(e) =>
                                  changeCompanyRole(
                                    u.id,
                                    c.id,
                                    e.target.value as SelectValue | "none",
                                  )
                                }
                                className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs focus:border-lime outline-none disabled:opacity-50"
                              >
                                <option value="none">{t("users.noAccess")}</option>
                                {roleOpts.map((r) => (
                                  <option
                                    key={r.value}
                                    value={r.value}
                                    disabled={!isSuper && r.value === "admin"}
                                  >
                                    {r.label}
                                  </option>
                                ))}
                              </select>
                              {/* F-acesso-por-menu — papel de portal: escolher os
                                  menus do Gestion que a pessoa vê/opera nesta empresa. */}
                              {(() => {
                                const rv = roleByCompany.get(c.id);
                                const portal =
                                  rv === "manager" ||
                                  rv === "technician" ||
                                  rv === "technician:montage" ||
                                  rv === "client";
                                if (!portal) return null;
                                const atuais = u.menuGrants.filter((g) => g.companyId === c.id);
                                return (
                                  <button
                                    type="button"
                                    disabled={busy || !u.canManage}
                                    onClick={() =>
                                      setGrantsFor(
                                        grantsFor?.userId === u.id && grantsFor.companyId === c.id
                                          ? null
                                          : { userId: u.id, companyId: c.id },
                                      )
                                    }
                                    className={`rounded-md border px-2 py-1 text-[11px] ${
                                      atuais.length > 0
                                        ? "border-lime/50 text-lime"
                                        : "border-neutral-700 text-neutral-400"
                                    }`}
                                    title={t("users.menusTitle")}
                                    data-testid={`menu-grants-btn-${u.id}-${c.id}`}
                                  >
                                    {t("users.menusBtn")} ({atuais.length})
                                  </button>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      )}
                      {grantsFor?.userId === u.id && (
                        <MenuGrantsEditor
                          key={`${u.id}-${grantsFor.companyId}`}
                          companyName={
                            companies.find((c) => c.id === grantsFor.companyId)?.name ?? ""
                          }
                          initial={u.menuGrants.filter((g) => g.companyId === grantsFor.companyId)}
                          busy={setMenuGrantsMut.isPending}
                          onCancel={() => setGrantsFor(null)}
                          onSave={(grants) =>
                            setMenuGrantsMut.mutate({
                              companyId: grantsFor.companyId,
                              userId: u.id,
                              grants,
                            })
                          }
                        />
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {u.active ? (
                        <span className="inline-block rounded bg-lime/20 text-lime px-2 py-0.5 text-xs">
                          {t("users.statusActive")}
                        </span>
                      ) : (
                        <span className="inline-block rounded bg-red-500/20 text-red-300 px-2 py-0.5 text-xs">
                          {t("users.statusInactive")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {/* F-users-compact — ações em UMA linha de ícones (tooltip
                          no title); a linha fica fina como o CEO pediu. */}
                      {!travado && (
                        <div className="inline-flex items-center gap-1">
                          {!u.isSuperAdmin && (
                            <IconBtn
                              label="✎"
                              title={t("users.editTitle")}
                              onClick={() =>
                                setEditing({
                                  id: u.id,
                                  name: u.name,
                                  email: u.email,
                                  emailLocked: !isSuper && u.lastLoginAt != null,
                                })
                              }
                            />
                          )}
                          {u.active && !u.isSuperAdmin && (
                            <IconBtn
                              label="✉"
                              title={t("users.sendLinkTitle")}
                              disabled={sendLinkMut.isPending}
                              onClick={() => handleSendLink(u)}
                              testId={`user-send-link-${u.id}`}
                            />
                          )}
                          {isSuper && u.active && !u.isSuperAdmin && (
                            <IconBtn
                              label="👁"
                              title={t("users.impersonateBtnTitle")}
                              disabled={impersonateMut.isPending}
                              onClick={() => handleImpersonate(u)}
                            />
                          )}
                          {isSuper && !u.isSuperAdmin && (
                            <IconBtn
                              label="🔑"
                              title={t("users.resetPwBtnTitle")}
                              disabled={resetPasswordMut.isPending}
                              onClick={() => void handleResetPassword(u)}
                            />
                          )}
                          <IconBtn
                            label={u.active ? "⏻" : "▶"}
                            title={
                              u.active ? t("users.deactivateAccount") : t("users.reactivateAccount")
                            }
                            disabled={setActiveMut.isPending}
                            onClick={() => setActiveMut.mutate({ userId: u.id, active: !u.active })}
                            testId={`user-toggle-active-${u.id}`}
                          />
                          {/* F-user-delete — só conta INATIVA e não-super. */}
                          {isSuper && !u.active && !u.isSuperAdmin && (
                            <IconBtn
                              label="🗑"
                              title={t("users.deleteBtn")}
                              danger
                              disabled={softDeleteMut.isPending}
                              onClick={() => {
                                void modals
                                  .confirm({
                                    title: t("users.deleteConfirmTitle"),
                                    message: `${t("users.deleteConfirmPre")} ${u.name} (${u.email})? ${t("users.deleteConfirmSuf")}`,
                                    tone: "danger",
                                    confirmLabel: t("users.deleteBtn"),
                                  })
                                  .then((ok) => {
                                    if (ok) softDeleteMut.mutate({ userId: u.id });
                                  });
                              }}
                            />
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="w-full max-w-sm space-y-3 rounded-xl border border-neutral-700 bg-neutral-900 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold">{t("users.editTitle")}</h2>
            <Field label={t("users.nameLabel")}>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className={inputCls + " w-full"}
              />
            </Field>
            <Field label={t("users.emailLabel")}>
              <input
                type="email"
                name="edit-user-email"
                autoComplete="off"
                data-lpignore="true"
                value={editing.email}
                disabled={editing.emailLocked}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                className={inputCls + " w-full disabled:opacity-60"}
              />
              {editing.emailLocked && (
                <span className="mt-1 block text-[11px] text-neutral-500">
                  {t("users.emailLockedHint")}
                </span>
              )}
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setEditing(null)}
                className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm hover:border-neutral-500"
              >
                {t("users.cancelBtn")}
              </button>
              <button
                onClick={() =>
                  updateProfileMut.mutate({
                    userId: editing.id,
                    name: editing.name.trim(),
                    email: editing.email.trim(),
                  })
                }
                disabled={
                  updateProfileMut.isPending || !editing.name.trim() || !editing.email.trim()
                }
                className="rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-on-lime disabled:opacity-50"
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {linkResultado && (
        <SenhaLinkModal resultado={linkResultado} onClose={() => setLinkResultado(null)} />
      )}

      <div className="rounded-md border border-neutral-800 bg-neutral-900/40 p-3 text-xs text-neutral-500 space-y-1">
        <p className="font-medium text-neutral-400">{t("users.levelsTitle")}</p>
        <p>
          <span className="text-neutral-300">{t("users.roleAdmin")}</span>{" "}
          {t("users.levelAdminDesc")}
        </p>
        <p>
          <span className="text-neutral-300">{t("users.roleUser")}</span> {t("users.levelUserDesc")}
        </p>
        <p>
          <span className="text-neutral-300">{t("users.roleViewer")}</span>{" "}
          {t("users.levelViewerDesc")}
        </p>
        <p>
          <span className="text-neutral-300">{t("users.superAdmin")}</span>{" "}
          {t("users.levelSuperAdminDesc")}
        </p>
      </div>
    </Shell>
  );
}

/**
 * F-acesso-por-menu (CEO 2026-10-12) — editor dos menus do Gestion de um
 * usuário de portal numa empresa: por menu, nada / ver / operar.
 */
function MenuGrantsEditor({
  companyName,
  initial,
  busy,
  onCancel,
  onSave,
}: {
  companyName: string;
  initial: Array<{ menu: string; level: string }>;
  busy: boolean;
  onCancel: () => void;
  onSave: (grants: Array<{ menu: ErpMenu; level: MenuGrantLevel }>) => void;
}) {
  const t = useT();
  const [levels, setLevels] = useState<Record<string, MenuGrantLevel | "none">>(() =>
    Object.fromEntries(
      ERP_MENUS.map((m) => {
        const g = initial.find((x) => x.menu === m);
        return [m, g ? (g.level as MenuGrantLevel) : "none"];
      }),
    ),
  );
  const labelKey: Record<ErpMenu, string> = {
    painel: "users.menuPainel",
    dossiers: "users.menuDossiers",
    devis: "users.menuDevis",
    clientes: "users.menuClientes",
    chantiers: "users.menuChantiers",
    agenda: "users.menuAgenda",
    veiculos: "users.menuVeiculos",
    tecnicos: "users.menuTecnicos",
    faturas: "users.menuFaturas",
  };
  return (
    <div
      className="mt-2 rounded-lg border border-lime/30 bg-neutral-950 p-3"
      data-testid="menu-grants-editor"
    >
      <p className="text-xs font-semibold text-lime">
        {t("users.menusTitle")} · {companyName}
      </p>
      <p className="mb-2 text-[11px] text-neutral-500">{t("users.menusHint")}</p>
      {/* CEO 2026-10-12 (print): em 3 colunas os nomes encostavam nos selects
          ("Tableau de bord" quebrava, "Devis" por cima do campo). Uma linha
          por menu: nome inteiro à esquerda, 3 botões à direita. */}
      <table className="w-full text-xs">
        <tbody>
          {ERP_MENUS.map((m) => (
            <tr key={m} className="border-t border-neutral-800" data-testid={`menu-grant-${m}`}>
              <td className="whitespace-nowrap py-1.5 pr-4">
                {t(labelKey[m] as Parameters<typeof t>[0])}
              </td>
              <td className="py-1.5 text-right">
                <div className="inline-flex overflow-hidden rounded-md border border-neutral-700">
                  {(["none", "view", "operate"] as const).map((lv) => {
                    const on = (levels[m] ?? "none") === lv;
                    return (
                      <button
                        key={lv}
                        type="button"
                        disabled={busy}
                        aria-pressed={on}
                        onClick={() => setLevels((prev) => ({ ...prev, [m]: lv }))}
                        className={`whitespace-nowrap px-2.5 py-1 text-[11px] ${
                          lv !== "none" ? "border-l border-neutral-700" : ""
                        } ${
                          on
                            ? lv === "none"
                              ? "bg-neutral-700 text-neutral-100"
                              : "bg-lime text-on-lime font-semibold"
                            : "text-neutral-400 hover:text-neutral-200"
                        }`}
                        data-testid={`menu-grant-${m}-${lv}`}
                      >
                        {lv === "none"
                          ? t("users.menusNone")
                          : lv === "view"
                            ? t("users.menuLevelView")
                            : t("users.menuLevelOperate")}
                      </button>
                    );
                  })}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border border-neutral-700 px-3 py-1 text-xs text-neutral-300"
        >
          {t("users.cancelBtn")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            onSave(
              ERP_MENUS.filter((m) => levels[m] !== "none").map((m) => ({
                menu: m,
                level: levels[m] as MenuGrantLevel,
              })),
            )
          }
          className="rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime"
          data-testid="menu-grants-save"
        >
          {t("users.menusSave")}
        </button>
      </div>
    </div>
  );
}

function IconBtn({
  label,
  title,
  onClick,
  disabled,
  danger,
  testId,
}: {
  label: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  testId?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid={testId}
      className={`rounded-md border px-2 py-1 text-sm leading-none disabled:opacity-40 ${
        danger
          ? "border-red-500/40 text-red-300 hover:border-red-500"
          : "border-neutral-800 hover:border-lime"
      }`}
    >
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] text-neutral-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Shell({ title: _title, children }: { title: string; children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-6xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}
